// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { imageCache } from '../utils/imageCache';

// Default asset icon - you can replace with your preferred icon
const DefaultAssetIcon = ({ className }) => (
  <div className={`bg-eai-surface-active rounded flex items-center justify-center ${className}`}>
    <svg className="w-1/2 h-1/2 text-eai-ink-faint" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
    </svg>
  </div>
);

interface AssetImageProps {
  asset?: any; // Accept asset object
  imageUrl?: string;
  assetName?: string;
  size?: number;
  className?: string;
  onClick?: () => void;
  fallbackIcon?: React.ReactNode;
}

const AssetImage: React.FC<AssetImageProps> = ({ 
  asset,
  imageUrl, 
  assetName = 'Asset', 
  size = 128, 
  className = '',
  onClick,
  fallbackIcon 
}) => {
  // Extract imageUrl from asset if provided
  const actualImageUrl = imageUrl || asset?.imageUrl;
  const actualAssetName = assetName || asset?.name || 'Asset';
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (actualImageUrl && !imageError) {
        setLoading(true);
        try {
          // Use the image cache instead of direct getUrl call
          const cachedUrl = await imageCache.getSignedUrl(actualImageUrl);

          if (cachedUrl) {
            setSignedUrl(cachedUrl);
          } else {
            setImageError(true);
          }
        } catch (error) {
          console.error(`AssetImage: Error fetching asset image URL for ${actualAssetName}:`, error);
          setImageError(true);
        } finally {
          setLoading(false);
        }
      } else {
        // No imageUrl provided - this is normal for assets without images console.log(`AssetImage: No imageUrl found for ${actualAssetName}, actualImageUrl:`, actualImageUrl);
      }
    };

    // Reset states when imageUrl changes
    setImageError(false);
    setSignedUrl(null);
    fetchSignedUrl();
  }, [actualImageUrl]);

  const containerClasses = `
    inline-block relative overflow-hidden rounded-eai-card border border-eai-border
    ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
    ${className}
  `;

  const imageClasses = 'w-full h-full object-cover';

  if (loading) {
    return (
      <div 
        className={containerClasses}
        style={{ width: size, height: size }}
      >
        <div className="w-full h-full bg-eai-surface-hover animate-pulse rounded-eai-card"></div>
      </div>
    );
  }

  if (signedUrl && !imageError) {
    return (
      <div 
        className={containerClasses}
        style={{ width: size, height: size }}
        onClick={onClick}
      >
        <img
          src={signedUrl}
          alt={actualAssetName}
          className={imageClasses}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // Fallback to custom icon or default icon
  return (
    <div 
      className={containerClasses}
      style={{ width: size, height: size }}
      onClick={onClick}
      title={actualAssetName}
    >
      {fallbackIcon ? (
        <div className="w-full h-full bg-eai-surface-hover rounded-eai-card flex items-center justify-center">
          {fallbackIcon}
        </div>
      ) : (
        <DefaultAssetIcon className="w-full h-full" />
      )}
    </div>
  );
};

export default AssetImage;